import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import CustomerCombobox from '../../../components/CustomerCombobox'
import SpreadsheetPreview from '../../../components/SpreadsheetPreview'
import { useCustomersByRole } from '../../../hooks/useCustomersByRole'
import {
  createTemplate, updateTemplate, getTemplateById, getTemplatesForCustomer, applyStrip, computeStructureSignature,
  deriveAnchors,
} from '../../../api/invoiceTemplates'
import type { InvoiceTemplate, SheetAssignments, TemplateSignature } from '../../../api/invoiceTemplates'
import {
  runAIFieldMappingSetupFromRows, readExcelRowsMultiSheet, readExcelRows,
  listWorkbookSheetNames, readNamedSheets, readMultiFileRows,
} from '../../../api/parseCommercialInvoiceFile'
import type { SheetInfo } from '../../../api/parseCommercialInvoiceFile'

type Step = 'select-customer' | 'structure-type' | 'upload' | 'assign-sheets' | 'processing' | 'confirm' | 'saving'
type StructureType = 'single_sheet' | 'multi_sheet' | 'two_files'

// Z42.2.2: asked up front (Step 2, per Gary) so the upload step can ask for
// exactly the right thing -- 1 file, 1 file + a "which sheet is which?" pick,
// or 2 separately-labeled files -- instead of always guessing at layout
// after the fact.
const STRUCTURE_TYPE_OPTIONS: { value: StructureType; label: string; description: string }[] = [
  { value: 'single_sheet', label: 'Single sheet', description: 'Invoice + Packing List combined in one sheet, one file' },
  { value: 'multi_sheet', label: 'Multi-sheet in one file', description: 'Invoice sheet + Packing List sheet in the same Excel file' },
  { value: 'two_files', label: 'Two separate files', description: 'One Invoice file + one Packing List file' },
]

interface HeaderDraft {
  status: 'confirmed' | 'skipped' | 'pending'
  row: number | null
  col: number | null
  strip_before: string
  strip_after: string
}

interface ItemColDraft {
  status: 'confirmed' | 'skipped' | 'pending'
  col: number | null
}

type PickTarget = { kind: 'header'; key: string } | { kind: 'item_col'; key: string } | { kind: 'start_row' }

// Row/col here are 0-indexed positions into the rows[][] grid -- matching
// field_mappings' own semantics exactly (see claudeInvoiceParser.ts /
// parse-invoice edge function) -- NOT spreadsheet A1 notation, so what staff
// confirms here is exactly what gets replayed by parseUsingTemplate later.
const HEADER_FIELDS: { key: string; label: string }[] = [
  { key: 'invoice_no', label: 'Invoice No' },
  { key: 'invoice_date', label: 'Invoice Date' },
  { key: 'shipper', label: 'Shipper Name' },
  { key: 'shipper_address', label: 'Shipper Address' },
  { key: 'consignee', label: 'Consignee Name' },
  { key: 'consignee_address', label: 'Consignee Address' },
  { key: 'vat_no', label: 'VAT No' },
  { key: 'eori', label: 'EORI' },
  { key: 'currency', label: 'Currency' },
  { key: 'incoterms', label: 'Incoterm' },
  { key: 'country_of_origin', label: 'Country of Origin' },
  { key: 'total_amount', label: 'Total Amount' },
  { key: 'gross_weight_kg', label: 'Total Gross Weight' },
  { key: 'net_weight_kg', label: 'Total Net Weight' },
  { key: 'total_cbm', label: 'Total CBM' },
  { key: 'packages', label: 'Total Packages (Ctns)' },
]

// These 5 fields default to being computed from the parsed item rows (sum of
// each item's own value) rather than read from a fixed cell -- robust to a
// totals row that moves depending on how many line items the invoice has.
// Only relevant when staff hasn't picked a specific cell for the field (see
// parseUsingTemplate's DEFAULT_TOTAL_ITEM_FIELDS) -- an explicit pick always
// wins.
const DERIVE_DEFAULT_FIELDS = new Set(['total_amount', 'gross_weight_kg', 'net_weight_kg', 'total_cbm', 'packages'])

const ITEM_COLUMN_FIELDS: { key: string; label: string }[] = [
  { key: 'goods_description', label: 'Description' },
  { key: 'hs_code', label: 'HS Code' },
  { key: 'pcs', label: 'PCS' },
  { key: 'ctns', label: 'Cartons' },
  { key: 'gross_weight', label: 'Gross Weight' },
  { key: 'net_weight', label: 'Net Weight' },
  { key: 'cbm', label: 'CBM' },
  { key: 'unit_value', label: 'Unit Value' },
  { key: 'amount', label: 'Amount' },
]

function draftsFromMappings(fm: any, defaultStatus: 'confirmed' | 'pending') {
  const header: Record<string, HeaderDraft> = {}
  for (const f of HEADER_FIELDS) {
    const spec = fm?.[f.key]
    if (spec && typeof spec.row === 'number' && typeof spec.col === 'number') {
      header[f.key] = { status: defaultStatus, row: spec.row, col: spec.col, strip_before: spec.strip_before || '', strip_after: spec.strip_after || '' }
    } else {
      header[f.key] = { status: 'skipped', row: null, col: null, strip_before: '', strip_after: '' }
    }
  }

  const items: Record<string, ItemColDraft> = {}
  const itemTable = fm?.item_table
  for (const f of ITEM_COLUMN_FIELDS) {
    const spec = itemTable?.columns?.[f.key]
    const col = typeof spec === 'object' && spec !== null ? spec.col : spec
    items[f.key] = typeof col === 'number' ? { status: defaultStatus, col } : { status: 'skipped', col: null }
  }

  const startRow = typeof itemTable?.start_row === 'number'
    ? { status: defaultStatus as 'confirmed' | 'pending', row: itemTable.start_row }
    : { status: 'skipped' as const, row: null as number | null }

  return { header, items, startRow }
}

export default function TemplateWizard() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEdit = !!id

  const [step, setStep] = useState<Step>(isEdit ? 'upload' : 'select-customer')
  const [error, setError] = useState('')
  const [templateName, setTemplateName] = useState('')
  const [selectedCustomerId, setSelectedCustomerId] = useState('')
  const [existingTemplateId, setExistingTemplateId] = useState<string | null>(null)
  const { customers } = useCustomersByRole('Cus')
  const selectedCustomer = customers.find(c => c.id === selectedCustomerId) || null

  const [file, setFile] = useState<File | null>(null)
  const [plFile, setPlFile] = useState<File | null>(null)
  const [rows, setRows] = useState<unknown[][] | null>(null)
  const [sheets, setSheets] = useState<SheetInfo[]>([])
  const [structureType, setStructureType] = useState<StructureType | null>(null)
  const [sheetNameOptions, setSheetNameOptions] = useState<string[]>([])
  const [invoiceSheetName, setInvoiceSheetName] = useState('')
  const [plSheetName, setPlSheetName] = useState('')
  const [signatureShipperText, setSignatureShipperText] = useState('')
  const [signatureFileNamePattern, setSignatureFileNamePattern] = useState('')

  const [headerDrafts, setHeaderDrafts] = useState<Record<string, HeaderDraft>>({})
  const [itemDrafts, setItemDrafts] = useState<Record<string, ItemColDraft>>({})
  const [startRowDraft, setStartRowDraft] = useState<{ status: 'confirmed' | 'skipped' | 'pending'; row: number | null }>({ status: 'skipped', row: null })
  const [pickTarget, setPickTarget] = useState<PickTarget | null>(null)
  const [duplicateWarning, setDuplicateWarning] = useState<InvoiceTemplate | null>(null)

  // Edit mode: load the existing template's mappings, pre-confirmed, and skip
  // straight to the "upload a sample so we have something to click on" step.
  // structure_type/sheet_assignments/signature come along too -- null
  // structure_type means a pre-Z42.2.2 template, which keeps using the old
  // generic auto-detecting upload (see handleLegacyEditFile).
  useEffect(() => {
    if (!id) return
    ;(async () => {
      const t = await getTemplateById(id)
      if (!t) { setError('Template not found'); return }
      setExistingTemplateId(t.id ?? null)
      setSelectedCustomerId(t.customer_id)
      setTemplateName(t.name)
      setStructureType(t.structure_type ?? null)
      setInvoiceSheetName(t.sheet_assignments?.invoice?.sheetName ?? '')
      setPlSheetName(t.sheet_assignments?.pl?.sheetName ?? '')
      setSignatureShipperText(t.signature?.shipperText ?? '')
      setSignatureFileNamePattern(t.signature?.fileNamePattern ?? '')
      const d = draftsFromMappings(t.field_mappings, 'confirmed')
      setHeaderDrafts(d.header)
      setItemDrafts(d.items)
      setStartRowDraft(d.startRow)
    })()
  }, [id])

  // Shared tail of every upload path below: stores the rows/sheets that got
  // read, runs a fresh AI proposal against them (new templates only -- edit
  // mode reuses the existing field_mappings loaded on mount instead), and
  // moves to the confirm/mapping step.
  async function finishReadingRows(newRows: unknown[][], newSheets: SheetInfo[]) {
    setRows(newRows)
    setSheets(newSheets)
    if (!isEdit) {
      const parsed = await runAIFieldMappingSetupFromRows(newRows)
      const d = draftsFromMappings(parsed.field_mappings, 'pending')
      setHeaderDrafts(d.header)
      setItemDrafts(d.items)
      setStartRowDraft(d.startRow)
      if (!signatureShipperText && parsed.header.shipper) setSignatureShipperText(parsed.header.shipper)
      if (!templateName && selectedCustomer) {
        setTemplateName(`${selectedCustomer.code} – ${parsed.header.shipper || 'template'} 1`)
      }
    }
    setStep('confirm')
  }

  // Pre-Z42.2.2 templates (structure_type null) being re-uploaded for edit --
  // preserves the exact old behavior (auto-detecting multi-sheet reader, no
  // fresh AI call, just re-display against the existing mappings).
  async function handleLegacyEditFile(f: File) {
    setFile(f)
    setError('')
    setStep('processing')
    try {
      const { rows: newRows, sheets: newSheets } = await readExcelRowsMultiSheet(f)
      setRows(newRows)
      setSheets(newSheets)
      setStep('confirm')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to process file')
      setStep('upload')
    }
  }

  async function handleSingleSheetFile(f: File) {
    setFile(f)
    setError('')
    setStep('processing')
    try {
      await finishReadingRows(await readExcelRows(f), [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to process file')
      setStep('upload')
    }
  }

  // multi_sheet: don't parse yet -- list the workbook's sheet names so staff
  // can say which is Invoice and which is Packing List first.
  async function handleMultiSheetFile(f: File) {
    setFile(f)
    setError('')
    setStep('processing')
    try {
      const names = await listWorkbookSheetNames(f)
      setSheetNameOptions(names)
      setInvoiceSheetName(prev => (prev && names.includes(prev) ? prev : names[0] || ''))
      setPlSheetName(prev => (prev && names.includes(prev) ? prev : names[1] || names[0] || ''))
      setStep('assign-sheets')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to read this file\'s sheets')
      setStep('upload')
    }
  }

  async function handleAssignSheetsContinue() {
    if (!file || !invoiceSheetName || !plSheetName) return
    setError('')
    setStep('processing')
    try {
      const { rows: newRows, sheets: newSheets } = await readNamedSheets(file, invoiceSheetName, plSheetName)
      await finishReadingRows(newRows, newSheets)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to read the assigned sheets')
      setStep('assign-sheets')
    }
  }

  async function handleTwoFilesContinue() {
    if (!file || !plFile) return
    setError('')
    setStep('processing')
    try {
      const { rows: newRows, sheets: newSheets } = await readMultiFileRows([file, plFile])
      await finishReadingRows(newRows, newSheets)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to process files')
      setStep('upload')
    }
  }

  function handleCellClick(row: number, col: number) {
    if (!pickTarget) return
    if (pickTarget.kind === 'header') {
      setHeaderDrafts(prev => ({ ...prev, [pickTarget.key]: { ...prev[pickTarget.key], status: 'confirmed', row, col } }))
    } else if (pickTarget.kind === 'item_col') {
      setItemDrafts(prev => ({ ...prev, [pickTarget.key]: { status: 'confirmed', col } }))
    } else {
      setStartRowDraft({ status: 'confirmed', row })
    }
    setPickTarget(null)
  }

  function confirmHeaderField(key: string) {
    setHeaderDrafts(prev => ({ ...prev, [key]: { ...prev[key], status: prev[key].row != null ? 'confirmed' : 'skipped' } }))
  }
  function skipHeaderField(key: string) {
    setHeaderDrafts(prev => ({ ...prev, [key]: { ...prev[key], status: 'skipped', row: null, col: null } }))
  }
  function confirmItemCol(key: string) {
    setItemDrafts(prev => ({ ...prev, [key]: { ...prev[key], status: prev[key].col != null ? 'confirmed' : 'skipped' } }))
  }
  function skipItemCol(key: string) {
    setItemDrafts(prev => ({ ...prev, [key]: { status: 'skipped', col: null } }))
  }

  const highlights: Record<string, string> = {}
  for (const [key, d] of Object.entries(headerDrafts)) {
    if (d.row == null || d.col == null) continue
    highlights[`${d.row},${d.col}`] = d.status === 'confirmed' ? 'rgba(34,197,94,0.25)' : 'rgba(245,158,11,0.25)'
    void key
  }

  function buildFieldMappings(): any {
    const fm: any = {}
    for (const f of HEADER_FIELDS) {
      const d = headerDrafts[f.key]
      if (d?.status === 'confirmed' && d.row != null && d.col != null) {
        const entry: any = { row: d.row, col: d.col }
        if (d.strip_before.trim()) entry.strip_before = d.strip_before.trim()
        if (d.strip_after.trim()) entry.strip_after = d.strip_after.trim()
        fm[f.key] = entry
      }
    }
    const itemCols: Record<string, number> = {}
    for (const f of ITEM_COLUMN_FIELDS) {
      const d = itemDrafts[f.key]
      if (d?.status === 'confirmed' && d.col != null) itemCols[f.key] = d.col
    }
    if (startRowDraft.status === 'confirmed' && startRowDraft.row != null && Object.keys(itemCols).length > 0) {
      const anchorField = ['hs_code', 'goods_description'].find(k => itemCols[k] != null) ?? Object.keys(itemCols)[0]
      fm.item_table = {
        start_row: startRowDraft.row,
        end_condition: `empty_col_${itemCols[anchorField]}`,
        columns: itemCols,
      }
    }
    return fm
  }

  function templateFileType(): 'xls' | 'xlsx' {
    return (file?.name.toLowerCase().endsWith('.xls') ? 'xls' : 'xlsx') as 'xls' | 'xlsx'
  }

  async function handleSaveTemplate() {
    if (!selectedCustomerId || !rows) return
    const trimmedName = templateName.trim()
    if (!trimmedName) { setError('Template name is required'); return }
    setError('')

    // Warn on a name collision with another of this customer's templates --
    // staff choose overwrite vs save-as-new rather than silently getting two
    // "KMS v1"s (or silently overwriting one they didn't mean to).
    const existing = await getTemplatesForCustomer(selectedCustomerId, templateFileType())
    const dup = existing.find(t => t.id !== existingTemplateId && t.name.trim().toLowerCase() === trimmedName.toLowerCase())
    if (dup) {
      setDuplicateWarning(dup)
      return
    }

    await saveTemplate(trimmedName, null)
  }

  // overwriteId: when set, updates that existing template instead of the one
  // being edited (or creating new) -- the "Overwrite existing" resolution.
  async function saveTemplate(name: string, overwriteId: string | null) {
    if (!selectedCustomerId || !rows) return
    setStep('saving')
    try {
      const field_mappings = deriveAnchors(rows, buildFieldMappings())
      const structure_signature = computeStructureSignature(rows, field_mappings)
      const fileType = templateFileType()
      const targetId = overwriteId ?? (isEdit ? existingTemplateId : null)

      // Z42.2.2: sheet_assignments only carries real information for
      // multi_sheet (the exact sheet names staff picked) -- single_sheet and
      // two_files replay don't need a name lookup (see readRowsForTemplate).
      const sheet_assignments: SheetAssignments = structureType === 'multi_sheet'
        ? { invoice: { sheetName: invoiceSheetName }, pl: { sheetName: plSheetName } }
        : {}
      const signature: TemplateSignature = {
        shipperText: signatureShipperText.trim() || undefined,
        fileNamePattern: signatureFileNamePattern.trim() || undefined,
      }
      const payload = {
        name, field_mappings, structure_signature, file_type: fileType,
        structure_type: structureType ?? null,
        sheet_assignments,
        signature,
      }

      if (targetId) {
        await updateTemplate(targetId, payload)
      } else {
        await createTemplate({ customer_id: selectedCustomerId, ...payload })
      }
      navigate('/customs/invoices/templates')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save template')
      setStep('confirm')
    }
  }

  function resolveDuplicateOverwrite() {
    if (!duplicateWarning?.id) return
    const id = duplicateWarning.id
    setDuplicateWarning(null)
    saveTemplate(templateName.trim(), id)
  }

  function resolveDuplicateSaveAsNew() {
    const newName = `${templateName.trim()} v2`
    setDuplicateWarning(null)
    setTemplateName(newName)
    saveTemplate(newName, null)
  }

  function statusBadge(status: string) {
    if (status === 'confirmed') return <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">✓ Confirmed</span>
    if (status === 'pending') return <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300">⚠ AI guess -- unconfirmed</span>
    return <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">∅ Not in this template</span>
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex items-center gap-3">
          <button onClick={() => navigate('/customs/invoices/templates')} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{isEdit ? 'Edit Template' : 'Template Setup Wizard'}</h1>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">{error}</div>
        )}

        {step === 'select-customer' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 max-w-lg">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Select Customer</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">Which customer is this template for?</p>
            <CustomerCombobox value={selectedCustomerId} onChange={setSelectedCustomerId} role="Cus" allowCreate />
            <button
              onClick={() => setStep('structure-type')}
              disabled={!selectedCustomerId}
              className="mt-6 w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
            >
              Continue
            </button>
          </div>
        )}

        {step === 'structure-type' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 max-w-lg">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">How is this shipper's CI/PL laid out?</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">This decides what you'll be asked to upload next.</p>
            <div className="space-y-3">
              {STRUCTURE_TYPE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setStructureType(opt.value)}
                  className={`w-full text-left p-4 border rounded-lg transition-colors ${
                    structureType === opt.value
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <p className="font-semibold text-gray-900 dark:text-white">{opt.label}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{opt.description}</p>
                </button>
              ))}
            </div>
            <button
              onClick={() => setStep('upload')}
              disabled={!structureType}
              className="mt-6 w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
            >
              Continue
            </button>
          </div>
        )}

        {(step === 'upload' || step === 'processing') && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 max-w-lg">
            {selectedCustomer && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Customer: <span className="font-semibold text-gray-700 dark:text-gray-300">{selectedCustomer.code} – {selectedCustomer.name}</span></p>
            )}
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Upload Sample File{structureType === 'two_files' ? 's' : ''}</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {structureType === 'single_sheet' && 'Upload the file -- Invoice and Packing List data are both on one sheet.'}
              {structureType === 'multi_sheet' && "Upload the file -- you'll pick which sheet is Invoice and which is Packing List next."}
              {structureType === 'two_files' && 'Upload the Invoice file and the Packing List file separately.'}
              {!structureType && (isEdit
                ? "Upload a current sample from this customer so we can show you the mapping against real cells."
                : 'Upload an unambiguous example commercial invoice (Excel only -- field_mappings is a row/col concept).')}
            </p>
            {step === 'processing' ? (
              <div className="text-center py-8">
                <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-gray-600 dark:text-gray-400">
                  {structureType === 'multi_sheet' && sheetNameOptions.length === 0 ? 'Reading sheet names...' : isEdit && !structureType ? 'Reading file...' : 'Parsing with AI...'}
                </p>
              </div>
            ) : structureType === 'two_files' ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Invoice (CI) file</label>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={e => { const f = e.target.files?.[0]; if (f) setFile(f) }}
                    className="w-full text-sm text-gray-700 dark:text-gray-300"
                  />
                  {file && <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">{file.name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Packing List file</label>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={e => { const f = e.target.files?.[0]; if (f) setPlFile(f) }}
                    className="w-full text-sm text-gray-700 dark:text-gray-300"
                  />
                  {plFile && <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">{plFile.name}</p>}
                </div>
                <button
                  onClick={handleTwoFilesContinue}
                  disabled={!file || !plFile}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
                >
                  Continue
                </button>
              </div>
            ) : (
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={e => {
                  const f = e.target.files?.[0]
                  if (!f) return
                  if (structureType === 'single_sheet') handleSingleSheetFile(f)
                  else if (structureType === 'multi_sheet') handleMultiSheetFile(f)
                  else handleLegacyEditFile(f)
                }}
                className="w-full text-sm text-gray-700 dark:text-gray-300"
              />
            )}
          </div>
        )}

        {step === 'assign-sheets' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 max-w-lg">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Which sheet is which?</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              "{file?.name}" has {sheetNameOptions.length} sheet{sheetNameOptions.length !== 1 ? 's' : ''}. Pick the Invoice sheet and the Packing List sheet.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Invoice sheet</label>
                <select
                  value={invoiceSheetName}
                  onChange={e => setInvoiceSheetName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {sheetNameOptions.map(name => <option key={name} value={name}>{name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Packing List sheet</label>
                <select
                  value={plSheetName}
                  onChange={e => setPlSheetName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {sheetNameOptions.map(name => <option key={name} value={name}>{name}</option>)}
                </select>
              </div>
              {invoiceSheetName && invoiceSheetName === plSheetName && (
                <p className="text-sm text-amber-600 dark:text-amber-400">Invoice and Packing List can't be the same sheet.</p>
              )}
              <button
                onClick={handleAssignSheetsContinue}
                disabled={!invoiceSheetName || !plSheetName || invoiceSheetName === plSheetName}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {(step === 'confirm' || step === 'saving') && rows && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: spreadsheet preview */}
            <div className="lg:sticky lg:top-6 self-start">
              {sheets.length > 0 && (
                <div className="mb-2 p-3 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg text-xs text-gray-600 dark:text-gray-400">
                  {sheets.map(s => (
                    <div key={`${s.name}-${s.startRow}`}>
                      Rows {s.startRow}–{s.startRow + s.rowCount - 1}: <span className="font-medium text-gray-800 dark:text-gray-200">"{s.name}"</span>
                      {' '}— detected as <span className="font-medium">{s.role === 'invoice' ? 'Invoice' : s.role === 'packingList' ? 'Packing List' : 'unknown'}</span>
                    </div>
                  ))}
                </div>
              )}
              {pickTarget && (
                <div className="mb-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-800 dark:text-blue-300 flex items-center justify-between">
                  <span>Click a cell to set this field</span>
                  <button onClick={() => setPickTarget(null)} className="text-blue-600 dark:text-blue-400 hover:underline text-xs">Cancel</button>
                </div>
              )}
              <SpreadsheetPreview rows={rows} maxRows={Math.min(rows.length, 120)} maxCols={15} onCellClick={handleCellClick} highlights={highlights} />
            </div>

            {/* Right: field-by-field confirmation */}
            <div className="space-y-6">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Template Name</label>
                <input
                  type="text"
                  value={templateName}
                  onChange={e => setTemplateName(e.target.value)}
                  placeholder="e.g. 箱单发票 KMS v1"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                {selectedCustomer && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Customer: {selectedCustomer.code} – {selectedCustomer.name}</p>}
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Auto-match signature (optional)</label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Checked first on future uploads, before falling back to layout matching.</p>
                <input
                  type="text"
                  value={signatureShipperText}
                  onChange={e => setSignatureShipperText(e.target.value)}
                  placeholder="Shipper name as it appears in the file"
                  className="w-full px-3 py-2 mb-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
                <input
                  type="text"
                  value={signatureFileNamePattern}
                  onChange={e => setSignatureFileNamePattern(e.target.value)}
                  placeholder="Filename pattern (optional)"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Header Fields</h3>
                <div className="space-y-3">
                  {HEADER_FIELDS.map(f => {
                    const d = headerDrafts[f.key]
                    if (!d) return null
                    const raw = d.row != null && d.col != null ? rows[d.row]?.[d.col] : null
                    const cleaned = raw != null ? applyStrip(raw, d) : null
                    return (
                      <div key={f.key} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-gray-800 dark:text-gray-200 text-sm">{f.label}</span>
                          <div className="flex items-center gap-2">
                            {d.row == null && DERIVE_DEFAULT_FIELDS.has(f.key) ? (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">Σ Computed from items</span>
                            ) : statusBadge(d.status)}
                          </div>
                        </div>
                        {d.row != null && d.col != null ? (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                            AI thinks Row {d.row}, Col {d.col} — raw: <span className="font-mono">{JSON.stringify(raw)}</span>
                            {cleaned !== raw && <> → cleaned: <span className="font-mono">{JSON.stringify(cleaned)}</span></>}
                          </p>
                        ) : DERIVE_DEFAULT_FIELDS.has(f.key) ? (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">No fixed cell picked — will be computed by summing this field across the parsed line items. Pick a cell below only if this file has a specific total cell you want used instead.</p>
                        ) : (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Not found in this document.</p>
                        )}
                        {d.row != null && d.col != null && typeof raw === 'string' && raw !== String(cleaned ?? '') && (
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <input type="text" placeholder="strip_before" value={d.strip_before}
                              onChange={e => setHeaderDrafts(prev => ({ ...prev, [f.key]: { ...prev[f.key], strip_before: e.target.value } }))}
                              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                            <input type="text" placeholder="strip_after" value={d.strip_after}
                              onChange={e => setHeaderDrafts(prev => ({ ...prev, [f.key]: { ...prev[f.key], strip_after: e.target.value } }))}
                              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                          </div>
                        )}
                        <div className="flex gap-2">
                          <button onClick={() => confirmHeaderField(f.key)} disabled={d.row == null}
                            className="px-2 py-1 text-xs bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded">✓ Confirm</button>
                          <button onClick={() => setPickTarget({ kind: 'header', key: f.key })}
                            className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700">↻ Pick different cell</button>
                          <button onClick={() => skipHeaderField(f.key)}
                            className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700">∅ Skip</button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Line Items</h3>

                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-800 dark:text-gray-200 text-sm">Item Table Start Row</span>
                    {statusBadge(startRowDraft.status)}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {startRowDraft.row != null ? `AI thinks Row ${startRowDraft.row}` : 'Not found -- pick the first line-item row.'}
                  </p>
                  <div className="flex gap-2">
                    <button onClick={() => setStartRowDraft(prev => ({ status: prev.row != null ? 'confirmed' : 'skipped', row: prev.row }))} disabled={startRowDraft.row == null}
                      className="px-2 py-1 text-xs bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded">✓ Confirm</button>
                    <button onClick={() => setPickTarget({ kind: 'start_row' })}
                      className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700">↻ Pick different row</button>
                  </div>
                </div>

                <div className="space-y-3">
                  {ITEM_COLUMN_FIELDS.map(f => {
                    const d = itemDrafts[f.key]
                    if (!d) return null
                    const samples = d.col != null && startRowDraft.row != null
                      ? [0, 1, 2].map(i => rows[startRowDraft.row! + i]?.[d.col!]).filter(v => v != null)
                      : []
                    return (
                      <div key={f.key} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-gray-800 dark:text-gray-200 text-sm">{f.label}</span>
                          {statusBadge(d.status)}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                          {d.col != null
                            ? <>AI thinks Col {d.col}{samples.length > 0 && <> — samples: <span className="font-mono">{samples.map(s => JSON.stringify(s)).join(', ')}</span></>}</>
                            : 'Not found in this document.'}
                        </p>
                        <div className="flex gap-2">
                          <button onClick={() => confirmItemCol(f.key)} disabled={d.col == null}
                            className="px-2 py-1 text-xs bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded">✓ Confirm</button>
                          <button onClick={() => setPickTarget({ kind: 'item_col', key: f.key })}
                            className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700">↻ Pick different cell</button>
                          <button onClick={() => skipItemCol(f.key)}
                            className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700">∅ Skip</button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              <button
                onClick={handleSaveTemplate}
                disabled={step === 'saving' || !templateName.trim()}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
              >
                {step === 'saving' ? 'Saving...' : 'Save Template'}
              </button>
            </div>
          </div>
        )}

        {duplicateWarning && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6 shadow-xl">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Template name already used</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                A template named <strong>"{duplicateWarning.name}"</strong> already exists for this customer. Overwrite it, or save this as a new template instead?
              </p>
              <div className="flex flex-col gap-2">
                <button onClick={resolveDuplicateOverwrite}
                  className="w-full px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-lg transition-colors text-sm">
                  Overwrite "{duplicateWarning.name}"
                </button>
                <button onClick={resolveDuplicateSaveAsNew}
                  className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors text-sm">
                  Save as new ("{templateName.trim()} v2")
                </button>
                <button onClick={() => setDuplicateWarning(null)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
